<template>
  <v-container fluid>
    <back-link-component :to="{ name: 'WorkRecords' }" :label="$tt('DocFormTabNameTaskRecordList')" />
    <v-row class="mt-0">
      <v-col cols="12" md="8">
        <h2 class="mb-1">
          {{ dateText }}
        </h2>
        <div class="mb-2">
          <v-chip x-small outlined color="purple" class="mr-2">{{ $tt('feed.derivedChip') }}</v-chip>
          <strong>{{ work ? work.name : '' }}</strong>
        </div>
        <!-- 走行・移動軌跡詳細と同じく、この記録の軌跡だけを表示する -->
        <gmap-map
          ref="googleMapRef"
          :center="getDefaultMapCenter()"
          :zoom="getDefaultZoomLevel()"
          map-type-id="roadmap"
          :options="{ streetViewControl: false, rotateControl: false, fullscreenControl: false }"
          style="height: 60vh"
        >
          <gmap-polyline
            v-for="(seg, i) in segments"
            :key="'seg-' + i"
            :path="seg"
            :options="{ strokeColor: '#8E24AA', strokeWeight: 4, strokeOpacity: 0.9, clickable: false }"
          />
        </gmap-map>
      </v-col>

      <!-- 属性 (走行・移動軌跡詳細の情報パネルと同じ体裁) -->
      <v-col cols="12" md="4">
        <v-sheet color="aid-bg-darkgrey" class="pa-4" v-if="work">
          <v-row no-gutters class="mb-3">
            <v-col cols="12" class="caption grey--text">{{ $tt('customOperationType') }}</v-col>
            <v-col cols="12">
              {{ getLocalizedOperationType(work.operation_type) }}
              <div v-if="work.name">{{ work.name }}</div>
            </v-col>
          </v-row>
          <v-divider class="mb-3" />
          <v-row no-gutters class="mb-3">
            <v-col cols="12" class="caption grey--text">{{ $tt('feed.recordStart') }}</v-col>
            <v-col cols="12">{{ dateText || '-----' }}</v-col>
          </v-row>
          <v-divider class="mb-3" />
          <v-row no-gutters class="mb-3">
            <v-col cols="12" class="caption grey--text">{{ $tt('customDistance') }}</v-col>
            <v-col cols="12">
              <span v-if="work.cache && work.cache.distance_m">
                {{ (work.cache.distance_m / 1000).toFixed(2) }} km
              </span>
              <span v-else>-----</span>
            </v-col>
          </v-row>
          <v-divider class="mb-3" />
          <v-row no-gutters class="mb-3">
            <v-col cols="12" class="caption grey--text">{{ $tt('feed.trajectories') }}</v-col>
            <v-col cols="12">{{ (work.sources || []).length }}</v-col>
          </v-row>
          <v-divider class="mb-3" />
          <v-row no-gutters class="mb-3">
            <v-col cols="12" class="caption grey--text">{{ $tt('feed.recordMemo') }}</v-col>
            <v-col cols="12">{{ work.memo || '-----' }}</v-col>
          </v-row>
          <v-layout justify-end>
            <v-btn color="error" text @click="deleteConfirm = true">
              <v-icon left>mdi-trash-can-outline</v-icon>
              {{ $tt('customDelete') }}
            </v-btn>
          </v-layout>
        </v-sheet>
      </v-col>
    </v-row>

    <v-dialog v-model="deleteConfirm" max-width="360">
      <v-card>
        <v-card-text class="pt-4">{{ $tt('feed.derivedDeleteConfirm') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteConfirm = false">{{ $tt('customCancel') }}</v-btn>
          <v-btn color="error" text @click="deleteWork()">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import GoogleMapMixin from '@/components/mixins/google-map-mixin'
import OperationTypeMixin from '@/components/mixins/operation-type-mixin'
import feedApiClient from '@/lib/feed-api-client'
import * as turf from '@turf/turf'

export default {
  name: 'WorkDetail',
  mixins: [GoogleMapMixin, OperationTypeMixin],
  data() {
    return {
      work: null,
      segments: [],
      deleteConfirm: false,
    }
  },
  computed: {
    dateText() {
      if (!this.work || !this.work.started_at) return ''
      const d = new Date(this.work.started_at)
      if (isNaN(d)) return ''
      return d.toLocaleString(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      try {
        const res = await feedApiClient.workRecords()
        this.work = (res.data.records || []).find(w => w.id === this.$route.params.id) || null
        if (!this.work) return
        // 派生記録の評価: sources の Trajectory を取得し range/clip を演算 (Work は座標を持たない)
        const paths = []
        for (const src of this.work.sources || []) {
          try {
            const r = await feedApiClient.getRoute(src.session_id, src.task_group_id)
            let pts = (r.data.route || []).map(p => ({ lat: p[0], lng: p[1] }))
            if (src.range && pts.length > 1) {
              const from = src.range.from_pct != null ? src.range.from_pct : 0
              const to = src.range.to_pct != null ? src.range.to_pct : 100
              pts = pts.slice(Math.floor((pts.length * from) / 100), Math.ceil((pts.length * to) / 100))
            }
            if (pts.length > 1) paths.push(pts)
          } catch (e) {
            // 経路なしはスキップ
          }
        }
        let finalPaths = paths
        if (this.work.kind === 'clip' && this.work.clip_polygon && this.work.clip_polygon.coordinates) {
          finalPaths = this.clipPaths(paths, this.work.clip_polygon.coordinates)
        }
        this.segments = finalPaths
        this.fitToSegments()
      } catch (e) {
        console.error(e.response || e)
      }
    },
    clipPaths(paths, polygonCoords) {
      // ポリゴン内の連続区間ごとに分割 (外に出た区間を結ばない)
      const poly = turf.polygon(polygonCoords)
      const out = []
      for (const path of paths) {
        let seg = []
        for (const p of path) {
          if (turf.booleanPointInPolygon(turf.point([p.lng, p.lat]), poly)) {
            seg.push(p)
          } else if (seg.length > 1) {
            out.push(seg)
            seg = []
          } else {
            seg = []
          }
        }
        if (seg.length > 1) out.push(seg)
      }
      return out
    },
    fitToSegments() {
      this.$refs.googleMapRef.$mapPromise.then(map => {
        map.setTilt(0)
        const bounds = new google.maps.LatLngBounds()
        let has = false
        for (const path of this.segments) {
          for (const p of path) {
            has = true
            bounds.extend(p)
          }
        }
        if (has) {
          map.fitBounds(bounds)
        }
      })
    },
    async deleteWork() {
      if (!this.work) return
      try {
        await feedApiClient.deleteWorkRecord(this.work.id)
        this.$router.push('/works')
      } catch (e) {
        console.error(e.response || e)
      } finally {
        this.deleteConfirm = false
      }
    },
  },
}
</script>
